import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Info } from 'lucide-react';

const PhaseInfo = () => {
  const [phaseInfo, setPhaseInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadPhaseInfo = async () => {
      try {
        const api = createAuthAPI();
        const response = await api.get('/client/phase-info', {
          signal: abortController.signal
        });
        if (isMounted) {
          setPhaseInfo(response.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          toast.error('Error al cargar información de fase');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPhaseInfo();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const loadPhaseInfo = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/client/phase-info');
      setPhaseInfo(response.data);
    } catch (error) {
      toast.error('Error al cargar información de fase');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Cargando información...</div>;
  }

  const info = phaseInfo?.info || {};

  return (
    <div className="space-y-6">
      <Card className="pharmacy-card bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-6 w-6 text-green-600" />
            Mi Fase Actual: Fase {phaseInfo?.fase_actual}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg font-semibold text-green-800">{info.nombre}</p>
          <p className="text-gray-700 mt-2">{info.descripcion}</p>
          {info.duracion && (
            <p className="text-sm text-gray-600 mt-2">Duración mínima: {info.duracion}</p>
          )}
          <p className="text-sm text-gray-600 mt-1">
            Inicio de esta fase: {new Date(phaseInfo?.fecha_inicio_fase).toLocaleDateString('es-ES')}
          </p>
        </CardContent>
      </Card>

      {/* Fase 0 Details */}
      {phaseInfo?.fase_actual === '0' && info.comidas && (
        <Card className="pharmacy-card">
          <CardHeader>
            <CardTitle>Distribución de Comidas</CardTitle>
            <div className="flex items-center gap-3 mt-3">
              <img src="/siken.jpg" alt="Siken" className="h-10 object-contain" />
              <img src="/fortepharma.jpg" alt="Fortepharma" className="h-10 object-contain" />
              <img src="/therascience.jpg" alt="Therascience" className="h-10 object-contain" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(info.comidas).map(([comida, descripcion]) => (
                <div key={comida} className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-semibold text-green-800">{comida}:</p>
                  <p className="text-gray-700">{descripcion}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ingredientes */}
      <Card className="pharmacy-card">
        <CardHeader>
          <CardTitle>Ingredientes Permitidos</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {info.ingredientes_libres && (
              <AccordionItem value="libres">
                <AccordionTrigger className="text-green-800 font-semibold">
                  Cantidad Libre
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1">
                    {info.ingredientes_libres.map((ing, index) => (
                      <li key={index} className="text-gray-700">{ing}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}

            {info.ingredientes_controlados && (
              <AccordionItem value="controlados">
                <AccordionTrigger className="text-amber-800 font-semibold">
                  Cantidad Controlada
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1">
                    {info.ingredientes_controlados.map((ing, index) => (
                      <li key={index} className="text-gray-700">{ing}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            )}

            {info.ingredientes_nuevos && (
              <AccordionItem value="nuevos">
                <AccordionTrigger className="text-blue-800 font-semibold">
                  Nuevos Ingredientes en esta Fase
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1">
                    {info.ingredientes_nuevos.map((ing, index) => (
                      <li key={index} className="text-gray-700">{ing}</li>
                    ))}
                  </ul>
                  {info.mantiene && (
                    <p className="text-sm text-gray-600 mt-3 italic">{info.mantiene}</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </CardContent>
      </Card>

      {/* Consejos Fase 0 */}
      {phaseInfo?.fase_actual === '0' && (
        <Card className="pharmacy-card bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-900">Consejos Importantes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {info.aceite && (
              <p className="text-blue-800">• <strong>Aceite:</strong> {info.aceite}</p>
            )}
            {info.agua && (
              <p className="text-blue-800">• <strong>Hidratación:</strong> {info.agua}</p>
            )}
            {info.ejercicio && (
              <p className="text-blue-800">• <strong>Ejercicio:</strong> {info.ejercicio}</p>
            )}
            <p className="text-blue-800">• Puedes añadir todo tipo de <strong>hierbas aromáticas y especias</strong></p>
          </CardContent>
        </Card>
      )}

      {/* Información especial Primeros 15 días - Fase 3 */}
      {phaseInfo?.fase_actual === '3' && info.primeros_15_dias && (
        <Card className="pharmacy-card bg-orange-50 border-orange-300 border-2">
          <CardHeader className="bg-orange-100">
            <CardTitle className="text-orange-900 flex items-center gap-2">
              🍞 {info.primeros_15_dias.titulo}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <p className="text-gray-800 font-semibold mb-2">
                ✅ {info.primeros_15_dias.descripcion}
              </p>
              <p className="text-gray-600 text-sm italic">
                📅 {info.primeros_15_dias.despues}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Menú Detox 10 Días - Fase 5 */}
      {phaseInfo?.fase_actual === '5' && (
        <Card className="pharmacy-card bg-purple-50 border-purple-300 border-2">
          <CardHeader className="bg-purple-100">
            <CardTitle className="text-purple-900 text-center text-xl">
              🥗 Plan Detox 10 días Forte pharma Laboratoires
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Descripción general */}
            <div className="bg-white p-4 rounded-lg border border-purple-200">
              <p className="text-purple-800 font-semibold mb-3">
                📋 Descripción del Plan
              </p>
              <p className="text-gray-700 text-sm mb-2">
                Menú detallado de dieta de 10 días que ofrece opciones saludables con una variedad de alimentos: verduras, proteínas magras, frutas y opciones integrales.
              </p>
              <ul className="text-gray-600 text-sm space-y-1 ml-4">
                <li>• <strong>Duración:</strong> 10 días</li>
                <li>• <strong>Distribución:</strong> 5 comidas diarias (desayuno, comida, merienda, cena y colación antes de dormir)</li>
                <li>• <strong>Alimentos principales:</strong> Infusiones, lácteos, frutas, verduras, legumbres, pescados y carnes magras</li>
              </ul>
            </div>

            {/* Menú día por día */}
            <div className="bg-white p-4 rounded-lg border-2 border-purple-300 space-y-4">
              <h3 className="text-purple-900 font-bold text-center text-lg mb-4">📅 Menú Diario</h3>
              
              {/* Día 1 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 1:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Infusión, Porridge de avena con bebida vegetal plátano y semillas de lino</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Salteado de verduras, Muslo de pollo de corral asado, Fruta</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Fruta, Puñado de almendras</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Té rooibos, Yogur natural con fresas y avellanas</p>
              </div>

              {/* Día 2 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 2:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Té verde, Tostadas integrales con aguacate y tomate, Yogur natural</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Calabacín relleno de verduras, Tortilla de patatas, Yogur natural</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Batido de bebida vegetal con plátano, Puñado de pistachos</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Crema de calabaza, Salmón a la plancha, Fruta</p>
              </div>

              {/* Día 3 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 3:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Té chai, Kéfir con arándanos y almendras, Piña natural</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Consomé de verduras, Lentejas a la jardinera, Yogur griego</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Infusión, Queso fresco con orejones</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Espárragos verdes, Huevo a la plancha, Cuajada</p>
              </div>

              {/* Día 4 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 4:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Bebida vegetal, Muesli de avena con frambuesas y nueces, Kiwi</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Vichissoise con picatostes tostados, Lubina al horno, Fruta</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Fruta, Puñado de avellanas</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Brócoli con patata al vapor, Filete de pavo, Fruta</p>
              </div>

              {/* Día 5 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 5:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Infusión, Tortilla de un huevo con orégano, Manzana</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Ensalada primavera, Garbanzos salteados con dados tomate, Yogur natural</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Té rojo, Kéfir con semillas de lino y mango deshidratado</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Alcachofas al horno, Sepia a la plancha, Fruta</p>
              </div>

              {/* Día 6 */}
              <div className="border-b border-purple-200 pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 6:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Bebida vegetal, Pan wasa con hummus y semillas de sésamo, Naranja</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Escalivada, Guisantes con calamares, Kéfir con moras</p>
                <p className="text-sm text-gray-700"><strong>Merienda:</strong> Batido de bebida vegetal con fresas, Puñado de nueces</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Ensalada de brotes, Mejillones al vapor, Yogur natural</p>
              </div>

              {/* Día 7 */}
              <div className="pb-3">
                <h4 className="font-bold text-purple-800 mb-2">Día 7:</h4>
                <p className="text-sm text-gray-700"><strong>Desayuno:</strong> Infusión, Yogur natural con frutos de bosque y quinoa inflada</p>
                <p className="text-sm text-gray-700"><strong>Comida:</strong> Puré de patata y calabacín, Bacalao al horno, Fruta</p>
                <p className="text-sm text-gray-700"><strong>Cena:</strong> Sopa vegetal, Tortilla de champiñones, Fruta</p>
              </div>
            </div>

            {/* Nota importante */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300">
              <p className="text-yellow-900 text-sm font-semibold mb-2">
                ⚠️ Descargo de responsabilidad
              </p>
              <p className="text-gray-700 text-xs italic">
                Esta información es un menú dietético sugerido. Siempre se debe consultar a un profesional de la salud o dietista antes de comenzar cualquier plan de alimentación nuevo, especialmente si existen condiciones médicas preexistentes.
              </p>
            </div>

            {/* Mensaje motivacional */}
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-green-800 text-sm font-semibold text-center">
                💚 ¡Lo importante es que comas bien y que lo disfrutes!
              </p>
            </div>

            {/* Tabla Visual del Menú - Recreación del PDF */}
            <div className="bg-white p-2 rounded-lg border-2 border-purple-300 overflow-x-auto">
              <p className="text-purple-900 font-bold text-center mb-3 text-lg">📊 Tabla del Menú - 10 Días</p>
              <div style={{ minWidth: '800px' }}>
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="border-2 border-gray-400 p-2 bg-pink-500 text-white font-bold"></th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 1</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 2</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 3</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 4</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 5</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 6</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 7</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 8</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 9</th>
                      <th className="border-2 border-gray-400 p-2 bg-green-800 text-white font-bold text-center">Día 10</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* DESAYUNO */}
                    <tr>
                      <td className="border-2 border-gray-400 p-2 bg-pink-500 text-white font-bold text-center">DESAYUNO</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión<br/>Porridge de avena con bebida vegetal plátano y semillas de lino</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Té verde<br/>Tostadas integrales con aguacate y tomate<br/>Yogur natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Té chai<br/>Kéfir con arándanos y almendras<br/>Piña natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Bebida vegetal<br/>Muesli de avena con frambuesas y nueces<br/>Kiwi</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión<br/>Tortilla de un huevo con orégano<br/>Manzana</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Bebida vegetal<br/>Pan wasa con hummus y semillas de sésamo<br/>Naranja</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión<br/>Yogur natural con frutos de bosque y quinoa inflada</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Bebida vegetal con granola y frutos secos</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Yogur natural con semillas de chía y fresas</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión<br/>Tortitas de arroz integral con salmón ahumado</td>
                    </tr>
                    {/* COMIDA */}
                    <tr>
                      <td className="border-2 border-gray-400 p-2 bg-pink-500 text-white font-bold text-center">COMIDA</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Salteado de verduras<br/>Muslo de pollo de corral asado<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Calabacín relleno de verduras<br/>Tortilla de patatas<br/>Yogur natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Consomé de verduras<br/>Lentejas a la jardinera<br/>Yogur griego</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Vichissoise con picatostes tostados<br/>Lubina al horno<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Ensalada primavera<br/>Garbanzos salteados con dados tomate<br/>Yogur natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Escalivada<br/>Guisantes con calamares<br/>Kéfir con moras</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Puré de patata y calabacín<br/>Bacalao al horno<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Ensalada de judías verdes con tomate y pepino<br/>Risotto de arroz integral con boletus y langostinos<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Espárragos trigueros con vinagreta<br/>Pollo al curry con verduras<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Brócoli al vapor<br/>Menestra de verduras<br/>Piña natural</td>
                    </tr>
                    {/* MERIENDA */}
                    <tr>
                      <td className="border-2 border-gray-400 p-2 bg-pink-500 text-white font-bold text-center">MERIENDA</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs"><span className="text-pink-600 font-semibold">Fruta</span><br/>Puñado de almendras</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Batido de bebida vegetal con plátano<br/>Puñado de pistachos</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión<br/>Queso fresco con orejones</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs"><span className="text-pink-600 font-semibold">Fruta</span><br/>Puñado de avellanas</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Té rojo<br/>Kéfir con semillas de lino y mango deshidratado</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Batido de bebida vegetal con fresas<br/>Puñado de nueces</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs"><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Infusión de hierbas</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Té verde</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Batido de frutas tropicales</td>
                    </tr>
                    {/* CENA */}
                    <tr>
                      <td className="border-2 border-gray-400 p-2 bg-pink-500 text-white font-bold text-center">CENA</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Té rooibos<br/>Yogur natural con fresas y avellanas</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Crema de calabaza<br/>Salmón a la plancha<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Espárragos verdes<br/>Huevo a la plancha<br/>Cuajada</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Brócoli con patata al vapor<br/>Filete de pavo<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Alcachofas al horno<br/>Sepia a la plancha<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Ensalada de brotes<br/>Mejillones al vapor<br/>Yogur natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Sopa vegetal<br/>Tortilla de champiñones<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Bacalao al horno<br/><span className="text-pink-600 font-semibold">Fruta</span></td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs">Crema de verduras<br/>Yogur natural</td>
                      <td className="border-2 border-gray-400 p-2 bg-white text-gray-800 text-xs"><span className="text-pink-600 font-semibold">Fruta</span><br/>Gelatina cuajada</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-500 text-xs text-center mt-2 italic">Desliza horizontalmente para ver todos los días →</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Principios OMS y Consideraciones - Fase 4 */}
      {phaseInfo?.fase_actual === '4' && (
        <>
          {/* Tabla de Menú Diario OMS */}
          <Card className="pharmacy-card bg-white border-green-300 border-2">
            <CardHeader className="bg-green-100">
              <CardTitle className="text-green-900 text-center">📋 Ejemplo de Menú Diario Saludable (según la OMS)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="p-3 text-left font-bold text-green-900 border-b-2 border-green-300">Comida</th>
                      <th className="p-3 text-left font-bold text-green-900 border-b-2 border-green-300">Ejemplo de Plato</th>
                      <th className="p-3 text-left font-bold text-green-900 border-b-2 border-green-300">Puntos Clave de la OMS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-green-200 hover:bg-green-50">
                      <td className="p-3 font-semibold text-green-800 align-top">🌅 Desayuno</td>
                      <td className="p-3 text-gray-700 align-top">
                        <strong>Porridge de avena con frutas y frutos secos.</strong><br/>
                        Cocinar la avena (cereal integral) con agua o leche/bebida vegetal baja en grasa. Servir con rodajas de plátano, algunas bayas frescas y un puñado pequeño de nueces o almendras naturales.
                      </td>
                      <td className="p-3 text-gray-600 align-top">
                        Cereales integrales, frutas (parte de las &gt;400g/día), grasas saludables (frutos secos).
                      </td>
                    </tr>
                    <tr className="border-b border-green-200 hover:bg-green-50">
                      <td className="p-3 font-semibold text-green-800 align-top">☕ Media Mañana</td>
                      <td className="p-3 text-gray-700 align-top">
                        <strong>Una manzana y un yogur natural sin azúcar.</strong>
                      </td>
                      <td className="p-3 text-gray-600 align-top">
                        Fruta adicional, lácteo bajo en grasa, sin azúcares añadidos.
                      </td>
                    </tr>
                    <tr className="border-b border-green-200 hover:bg-green-50">
                      <td className="p-3 font-semibold text-green-800 align-top">🍽️ Almuerzo</td>
                      <td className="p-3 text-gray-700 align-top">
                        <strong>Plato completo de lentejas con verduras y arroz integral.</strong><br/>
                        Guiso de lentejas (legumbre, fuente de proteína vegetal y fibra) cocinado con zanahoria, apio, cebolla y pimiento. Acompañar con una porción moderada de arroz integral. Aliñar con una cucharada de aceite de oliva virgen extra.
                      </td>
                      <td className="p-3 text-gray-600 align-top">
                        Legumbres, verduras, cereales integrales, aceite de oliva (grasa insaturada).
                      </td>
                    </tr>
                    <tr className="border-b border-green-200 hover:bg-green-50">
                      <td className="p-3 font-semibold text-green-800 align-top">🥪 Merienda</td>
                      <td className="p-3 text-gray-700 align-top">
                        <strong>Tostada de pan integral con aguacate y tomate.</strong><br/>
                        Una rebanada de pan integral con rodajas de aguacate y tomate natural triturado.
                      </td>
                      <td className="p-3 text-gray-600 align-top">
                        Cereales integrales, grasas saludables, verduras.
                      </td>
                    </tr>
                    <tr className="hover:bg-green-50">
                      <td className="p-3 font-semibold text-green-800 align-top">🌙 Cena</td>
                      <td className="p-3 text-gray-700 align-top">
                        <strong>Salmón al horno con espárragos y batata asada.</strong><br/>
                        Filete de salmón (pescado azul, fuente de omega-3) horneado con espárragos trigueros y media batata asada. Aliñar con aceite de oliva y hierbas aromáticas (eneldo, perejil) en lugar de sal.
                      </td>
                      <td className="p-3 text-gray-600 align-top">
                        Pescado (proteína y grasa saludable), verduras, tubérculos (con moderación), bajo en sal.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="pharmacy-card bg-green-50 border-green-200">
            <CardHeader>
              <CardTitle className="text-green-900">Principios de la OMS</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2">
                {info.principios_oms?.map((principio, index) => (
                  <li key={index} className="text-green-800">{principio}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {info.consideraciones && (
            <Card className="pharmacy-card bg-yellow-50 border-yellow-200">
              <CardHeader>
                <CardTitle className="text-yellow-900">Consideraciones Adicionales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-yellow-800">• <strong>Hidratación:</strong> {info.consideraciones.hidratacion}</p>
                <p className="text-yellow-800">• <strong>Métodos de cocción:</strong> {info.consideraciones.metodos_coccion}</p>
                <p className="text-yellow-800">• <strong>Sal y azúcar:</strong> {info.consideraciones.sal_azucar}</p>
                <p className="text-yellow-800">• <strong>Actividad física:</strong> {info.consideraciones.actividad_fisica}</p>
              </CardContent>
            </Card>
          )}

          {info.enlaces_interes && (
            <Card className="pharmacy-card bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-900">Enlaces de Interés</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {info.enlaces_interes.map((enlace, index) => (
                  <a 
                    key={index}
                    href={enlace.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                  >
                    • {enlace.titulo} →
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PhaseInfo;